import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRightLeft, Check, LogOut, Building2, ChevronDown, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useFirma } from "../context/FirmaContext";
import { MeseGiorniEsplosi, RecapGiorno } from "../components/MeseGiorniEsplosi";
import { ExportButton } from "../components/ExportButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { ConfirmUndoDialog } from "../components/ConfirmUndoDialog";
import { ProgrammaUscitaDialog, UsciteProgrammateBox, useUsciteProgrammate } from "../components/UsciteProgrammate";

const COLOR = "#0e7490";
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtLong = (iso) => { try { const d = new Date(iso + "T00:00:00"); const s = d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" }); return s.charAt(0).toUpperCase() + s.slice(1); } catch { return iso; } };
const relGiorni = (n) => n == null ? "" : n === 0 ? "oggi" : n === 1 ? "ieri" : `da ${n} giorni`;
const fmtShort = (iso) => { try { const d = new Date(iso + "T00:00:00"); return d.toLocaleDateString("it-IT", { day: "numeric", month: "short" }); } catch { return iso; } };
const fuoriDa = (rec) => {
  if (!rec?.data) return null;
  const start = new Date(`${rec.data}T${rec.ora || "00:00"}:00`);
  if (isNaN(start.getTime())) return null;
  const ms = Date.now() - start.getTime();
  const h = Math.floor(ms / 3600000);
  if (h < 24) return h <= 0 ? "da poco" : `da ${h} ${h === 1 ? "ora" : "ore"}`;
  const d = Math.floor(h / 24);
  return `da ${d} ${d === 1 ? "giorno" : "giorni"}`;
};
const sx = (sesso, male, female) => (String(sesso).toUpperCase() === "M" ? male : female);
const STATI = [
  { v: "presente", l: "Presente", Icon: Check, active: "bg-green-600 text-white", hover: "hover:bg-green-100", color: "#16a34a" },
  { v: "uscita", l: "Uscito", Icon: LogOut, active: "bg-amber-500 text-white", hover: "hover:bg-amber-100", color: "#d97706" },
  { v: "ospedale", l: "Ricoverato", Icon: Building2, active: "bg-red-600 text-white", hover: "hover:bg-red-100", color: "#dc2626" },
];
const statoInfo = (v) => STATI.find((s) => s.v === v) || STATI[0];

const EvCol = ({ ev, color }) => {
  if (!ev) return <div className="text-xs text-muted-foreground">—</div>;
  const times = ev.rientrato && ev.ora_rientro
    ? `Ore ${ev.ora || "--:--"} → rientro ${ev.ora_rientro}${ev.data_rientro && ev.data_rientro !== ev.data ? ` del ${ev.data_rientro}` : " (stesso giorno)"}`
    : `Ore ${ev.ora || "--:--"}${ev.rientro_previsto ? ` · rientro previsto tra ${ev.rientro_previsto}` : ""}`;
  return (
    <>
      <div className="text-xs leading-snug"><span className="text-[9px] uppercase text-muted-foreground font-semibold mr-1">Motivo:</span><span className="font-bold" style={{ color }}>{ev.motivo || "—"}</span></div>
      <div className="text-[10px] text-muted-foreground capitalize">{fmtLong(ev.data)} · {relGiorni(ev.giorni)}</div>
      <div className="text-[10px] text-muted-foreground leading-tight">{times}{ev.operatore ? ` · ✍️ ${ev.operatore}` : ""}</div>
    </>
  );
};

const SettimanaBox = ({ items = [], range }) => (
  <div className="rounded-2xl border-2 bg-white p-4 shadow-sm flex flex-col" style={{ borderColor: COLOR + "40" }} data-testid="mov-settimana-lista">
    <div className="flex items-center gap-2 mb-3">
      <span className="grid place-items-center h-7 w-7 rounded-lg" style={{ background: COLOR + "1f" }}><ArrowRightLeft size={16} style={{ color: COLOR }} /></span>
      <span className="font-head font-bold text-sm">Movimenti della settimana</span>
      {range && <span className="text-[11px] text-muted-foreground ml-auto">{fmtShort(range.da)} → {fmtShort(range.a)}</span>}
    </div>
    {items.length === 0 ? (
      <div className="text-sm text-muted-foreground">Nessun movimento negli ultimi 7 giorni.</div>
    ) : (
      <ul className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="mov-settimana-items">
        {items.map((it, i) => {
          const ric = it.tipo === "ricovero";
          const col = ric ? "#dc2626" : "#d97706";
          const azione = ric ? sx(it.sesso, "ricoverato", "ricoverata") : sx(it.sesso, "uscito", "uscita");
          const rientr = sx(it.sesso, "rientrato", "rientrata");
          return (
            <li key={i} className="flex items-start gap-2 text-sm leading-snug" data-testid="mov-settimana-item">
              {ric ? <Building2 size={15} className="mt-0.5 shrink-0" style={{ color: col }} /> : <LogOut size={15} className="mt-0.5 shrink-0" style={{ color: col }} />}
              <span>
                <b>{it.nome}</b>
                <span style={{ color: col }} className="font-semibold"> {azione}</span> il {fmtShort(it.data)}
                {it.motivo ? <span className="text-muted-foreground"> ({it.motivo})</span> : ""}
                {it.rientrato
                  ? <span className="text-emerald-700 font-semibold">, {rientr} il {fmtShort(it.data_rientro)}</span>
                  : <span className="text-red-700 font-semibold"> · non ancora {rientr}, fuori da {it.giorni_fuori ?? 0} {it.giorni_fuori === 1 ? "giorno" : "giorni"}</span>}
              </span>
            </li>
          );
        })}
      </ul>
    )}
  </div>
);

export default function EntrateUscite() {
  const { selectedStructure } = useAuth();
  const { canSign, signerName, mustUnlock } = useFirma();
  const qc = useQueryClient();
  const today = ymd(new Date());
  const [openDay, setOpenDay] = useState(today);
  const [mese, setMese] = useState(today.slice(0, 7));
  const [showDimessi, setShowDimessi] = useState(false);
  const [evt, setEvt] = useState(null); // { r, stato }
  const [fMotivo, setFMotivo] = useState("");
  const [fNum, setFNum] = useState("");
  const [fUnit, setFUnit] = useState("ore");
  const selDate = openDay || today;
  const [progOpen, setProgOpen] = useState(false);
  const [confirmCtx, setConfirmCtx] = useState(null);
  const { data: programmate = [] } = useUsciteProgrammate(14);
  const confermaProg = async (u) => {
    if (!canSign) { toast.error("Sblocca con la Firma Rapida (PIN) per confermare l'uscita"); return; }
    try { await api.post("/movimenti/conferma", { id: u.id, tipo: u.tipo, operatore: signerName }); qc.invalidateQueries(); toast.success(`${u.cognome} ${u.nome}: uscita confermata`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const annullaProg = (u) => setConfirmCtx({ nome: `${u.cognome} ${u.nome}`, titolo: "Annullare l'uscita programmata?", testo: "Vuoi davvero annullare l'uscita programmata di", ok: "Sì, annulla l'uscita", action: async () => {
    try { await api.post("/movimenti/annulla", { id: u.id, tipo: u.tipo }); qc.invalidateQueries({ queryKey: ["uscite-programmate"] }); toast.success("Uscita programmata annullata"); } catch { toast.error("Errore"); }
  } });
  const apriProg = () => { if (!canSign) { toast.error("Sblocca con la Firma Rapida (PIN) per programmare un'uscita"); return; } setProgOpen(true); };

  const { data: meseData } = useQuery({
    queryKey: ["mov-pres-mese", selectedStructure, mese],
    queryFn: async () => (await api.get("/presenza/mese", { params: { structure_id: selectedStructure, mese } })).data,
  });
  const { data } = useQuery({
    queryKey: ["mov-pres-day", selectedStructure, selDate],
    refetchInterval: 20000,
    queryFn: async () => (await api.get("/presenza/day", { params: { structure_id: selectedStructure, data: selDate } })).data,
  });
  const rows = data?.rows || [];
  const records = data?.records || {};

  const doRegister = async (r, stato, extra = {}) => {
    try {
      await api.post("/presenza/register", { ospite_id: r.ospite_id, structure_id: r.structure_id, data: selDate, stato, operatore: signerName, ...extra });
      qc.invalidateQueries(); // sincronizza scheda ospite, dashboard, lista ospiti ovunque
      toast.success(`Stato aggiornato: ${statoInfo(stato).l}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nel salvataggio"); }
  };

  const setStato = async (r, stato) => {
    if (!canSign) { toast.error("Sblocca con la Firma Rapida (PIN) per registrare"); return; }
    const cur = records[r.ospite_id]?.stato || "presente";
    if (cur === stato) return;
    if (stato === "uscita" || stato === "ospedale") {
      setFMotivo(""); setFNum(""); setFUnit("ore");
      setEvt({ r, stato });
      return;
    }
    await doRegister(r, "presente"); // rientro
  };

  const confermaEvento = async () => {
    if (!evt) return;
    const rp = fNum && Number(fNum) > 0 ? `${fNum} ${fUnit}` : "";
    await doRegister(evt.r, evt.stato, { motivo: fMotivo, rientro_previsto: rp });
    setEvt(null);
  };

  const isDimesso = (r) => !!(r.data_dimissione && String(r.data_dimissione).trim());
  const attivi = rows.filter((r) => !isDimesso(r));
  const dimessi = rows.filter(isDimesso);

  // recap del giorno
  let nPres = 0, nUsc = 0, nOsp = 0;
  const opCounts = {};
  attivi.forEach((r) => {
    const rec = records[r.ospite_id];
    const st = rec?.stato || "presente";
    if (st === "uscita") nUsc++; else if (st === "ospedale") nOsp++; else nPres++;
    if ((st === "uscita" || st === "ospedale") && rec?.operatore) opCounts[rec.operatore] = (opCounts[rec.operatore] || 0) + 1;
  });
  const operatoriGiorno = Object.entries(opCounts).map(([nome, count]) => ({ nome, count })).sort((a, b) => b.count - a.count);
  const dayStats = [
    { key: "pres", label: "Presenti", value: nPres, tone: "ok" },
    { key: "usc", label: "Usciti", value: nUsc, tone: "warn" },
    { key: "osp", label: "Ricoverati", value: nOsp, tone: "bad" },
  ];

  const recapMese = (
    <div className="gr-card p-4 mb-4" data-testid="mov-recap-mese">
      <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5"><ArrowRightLeft size={14} style={{ color: COLOR }} /> Recap del mese — movimenti</div>
      <div className="flex flex-wrap gap-2">
        <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold bg-amber-100 text-amber-700" data-testid="mov-recap-uscite">Uscite <span className="bg-amber-500 text-white rounded-full text-xs px-1.5 py-0.5 tabnum">{meseData?.uscite ?? 0}</span></span>
        <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold bg-red-100 text-red-700" data-testid="mov-recap-ricoveri">Ricoveri <span className="bg-red-600 text-white rounded-full text-xs px-1.5 py-0.5 tabnum">{meseData?.ospedale ?? 0}</span></span>
        {(meseData?.operatori || []).map((o) => (
          <span key={o.nome} className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold" style={{ background: COLOR + "18", color: COLOR }} data-testid={`mov-recap-op-${o.nome}`}>{o.nome} <span className="text-white rounded-full text-xs px-1.5 py-0.5 tabnum" style={{ background: COLOR }}>{o.count}</span></span>
        ))}
      </div>
    </div>
  );

  const exportCols = [
    { header: "Ospite", accessor: (r) => `${r.cognome} ${r.nome}` },
    { header: "Camera/zona", accessor: (r) => r.camera || "" },
    { header: "Stato", accessor: (r) => statoInfo(records[r.ospite_id]?.stato).l },
    { header: "Motivo", accessor: (r) => records[r.ospite_id]?.motivo || "" },
    { header: "Operatore", accessor: (r) => records[r.ospite_id]?.operatore || "" },
    { header: "Ultima uscita", accessor: (r) => r.ultima_uscita ? `${r.ultima_uscita.data}${r.ultima_uscita.motivo ? " - " + r.ultima_uscita.motivo : ""}${r.ultima_uscita.operatore ? " (" + r.ultima_uscita.operatore + ")" : ""}` : "" },
    { header: "Ultimo ricovero", accessor: (r) => r.ultimo_ricovero ? `${r.ultimo_ricovero.data}${r.ultimo_ricovero.motivo ? " - " + r.ultimo_ricovero.motivo : ""}${r.ultimo_ricovero.operatore ? " (" + r.ultimo_ricovero.operatore + ")" : ""}` : "" },
  ];

  const renderRow = (r, num) => {
    const rec = records[r.ospite_id];
    const st = rec?.stato || "presente";
    const dim = isDimesso(r);
    const info = statoInfo(st);
    return (
      <div key={r.ospite_id} className="gr-card p-3 flex flex-col xl:flex-row xl:items-center gap-2 xl:gap-3" data-testid={`mov-row-${r.ospite_id}`}>
        <div className="flex items-center gap-2 min-w-0 flex-1 w-full">
          <span className="shrink-0 grid place-items-center h-6 w-6 rounded-full bg-secondary text-muted-foreground text-[11px] font-bold tabnum">{num != null ? num : "–"}</span>
          <div className="min-w-0 flex-1">
            <div className="font-semibold flex items-center gap-2 flex-wrap">{r.cognome} {r.nome}{dim && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 shrink-0">Dimesso</span>}</div>
            {r.camera && <div className="text-[11px] font-semibold text-blue-600 mt-0.5">cam. {r.camera}</div>}
            {st !== "presente" && fuoriDa(rec) && (
              <div className="text-[11px] xl:hidden mt-1 font-bold" style={{ color: info.color }} data-testid={`mov-fuorida-m-${r.ospite_id}`}>{st === "ospedale" ? sx(r.sesso, "Ricoverato", "Ricoverata") : "Fuori"} {fuoriDa(rec)}</div>
            )}
            {(r.ultima_uscita || r.ultimo_ricovero) && (
              <div className="text-[11px] text-muted-foreground xl:hidden mt-1 space-y-0.5" data-testid={`mov-storico-m-${r.ospite_id}`}>
                {r.ultima_uscita && <div>Ultima uscita: <b className="text-amber-700">{r.ultima_uscita.motivo || "—"}</b> · {fmtLong(r.ultima_uscita.data)}{r.ultima_uscita.operatore ? ` · ✍️ ${r.ultima_uscita.operatore}` : ""}</div>}
                {r.ultimo_ricovero && <div>Ultimo ricovero: <b className="text-red-700">{r.ultimo_ricovero.motivo || "—"}</b> · {fmtLong(r.ultimo_ricovero.data)}{r.ultimo_ricovero.operatore ? ` · ✍️ ${r.ultimo_ricovero.operatore}` : ""}</div>}
              </div>
            )}
          </div>
        </div>
        <div className="hidden xl:flex items-start gap-5 shrink-0" data-testid={`mov-cols-${r.ospite_id}`}>
          <div className="w-32">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1 mb-0.5"><Clock size={11} className="text-cyan-700" /> Da quanto fuori</div>
            {st !== "presente" && fuoriDa(rec)
              ? <div className="text-sm font-bold leading-snug" style={{ color: info.color }} data-testid={`mov-fuorida-${r.ospite_id}`}>{st === "ospedale" ? sx(r.sesso, "Ricoverato", "Ricoverata") : "Fuori"} {fuoriDa(rec)}</div>
              : <div className="text-xs text-muted-foreground">—</div>}
          </div>
          <div className="w-52">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1 mb-0.5"><LogOut size={11} className="text-amber-600" /> Ultima uscita</div>
            <EvCol ev={r.ultima_uscita} color="#b45309" />
          </div>
          <div className="w-52">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1 mb-0.5"><Building2 size={11} className="text-red-600" /> Ultimo ricovero</div>
            <EvCol ev={r.ultimo_ricovero} color="#b91c1c" />
          </div>
        </div>
        <div className="flex items-center gap-1.5 w-full xl:w-auto">
          {STATI.map((s) => (
            <button key={s.v} onClick={() => setStato(r, s.v)}
              className={`h-11 flex-1 min-w-0 xl:flex-none px-2 sm:px-3 rounded-lg text-xs sm:text-sm font-semibold inline-flex items-center justify-center gap-1 sm:gap-1.5 transition-colors ${st === s.v ? s.active : `bg-secondary ${s.hover}`}`}
              data-testid={`mov-${s.v}-${r.ospite_id}`}><s.Icon size={15} /> {s.l}</button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <MeseGiorniEsplosi
      title="Entrate / Uscite" subtitle="Stato degli ospiti giorno per giorno · Presente / Uscito / Ricoverato (modificabile anche nei giorni passati, con firma)"
      Icon={ArrowRightLeft} color={COLOR}
      mese={mese} onMese={setMese} openDay={openDay} onOpenDay={setOpenDay}
      counts={meseData?.counts || {}} countLabel="uscite/ricoveri"
      monthTop={recapMese}
      detailRight={<ExportButton filename="entrate-uscite" title={`Entrate / Uscite · ${selDate}`} subtitle="" columns={exportCols} rows={rows} color={COLOR} testid="mov-export" />}
    >
      {recapMese}
      <div className="mb-4"><UsciteProgrammateBox items={programmate} onConferma={confermaProg} onAnnulla={annullaProg} onNuova={apriProg} testid="mov-programmate" /></div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4 items-start" data-testid="mov-recap-grid">
        <RecapGiorno operatori={operatoriGiorno} stats={dayStats} color={COLOR} opLabel="movimenti" mb={false} />
        <SettimanaBox items={data?.settimana_lista || []} range={data?.settimana_range} />
      </div>
      {mustUnlock && <p className="text-sm text-amber-600 mb-4" data-testid="mov-firma-warning">⚠️ Sblocca con la Firma Rapida (PIN) per modificare lo stato degli ospiti.</p>}
      <div className="space-y-2" data-testid="mov-giorno-list">
        {rows.length === 0 && <p className="text-sm text-muted-foreground">Nessun ospite</p>}
        {attivi.map((r, i) => renderRow(r, i + 1))}
        {dimessi.length > 0 && (
          <div data-testid="mov-dimessi-section">
            <button onClick={() => setShowDimessi((v) => !v)} data-testid="mov-toggle-dimessi" className="w-full flex items-center gap-2 text-left px-3 py-2.5 rounded-lg bg-orange-50 border border-orange-200 mt-2">
              <ChevronDown size={16} className={`text-orange-700 transition-transform ${showDimessi ? "" : "-rotate-90"}`} />
              <span className="font-bold text-sm text-orange-800">Dimessi</span>
              <span className="text-xs font-semibold text-orange-700 bg-orange-200 rounded-full px-2 py-0.5 tabnum">{dimessi.length}</span>
            </button>
            {showDimessi && <div className="space-y-2 mt-2">{dimessi.map((r) => renderRow(r, null))}</div>}
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-3">Di default ogni ospite risulta <b>Presente</b>. Imposta <b>Uscito</b> o <b>Ricoverato</b> quando serve: la modifica su <b>oggi</b> aggiorna in tempo reale la scheda ospite, i box della dashboard e la lista ospiti. Puoi correggere anche i giorni passati (resta la firma dell'operatore).</p>

      <Dialog open={!!evt} onOpenChange={(o) => !o && setEvt(null)}>
        <DialogContent data-testid="mov-evento-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {evt?.stato === "ospedale" ? <Building2 size={18} className="text-red-600" /> : <LogOut size={18} className="text-amber-600" />}
              {evt?.stato === "ospedale" ? "Ricovero ospedaliero" : "Uscita ospite"}
            </DialogTitle>
            <DialogDescription>{evt ? `${evt.r.cognome} ${evt.r.nome}` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold mb-1.5 block">Motivo</label>
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="mov-evento-motivi-rapidi">
                {(evt?.stato === "ospedale"
                  ? ["Accertamenti", "Pronto soccorso", "Ricovero programmato", "Day hospital", "Dimissione ospedaliera"]
                  : ["Uscita con i parenti", "Visita medica", "Accertamenti", "Passeggiata", "Permesso"]).map((m) => (
                  <button key={m} type="button" onClick={() => setFMotivo(m)}
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${fMotivo === m ? "text-white border-transparent" : "bg-secondary hover:bg-secondary/70 border-transparent"}`}
                    style={fMotivo === m ? { background: evt?.stato === "ospedale" ? "#dc2626" : "#d97706" } : {}}
                    data-testid={`mov-motivo-rapido-${m}`}>{m}</button>
                ))}
              </div>
              <Textarea rows={2} value={fMotivo} onChange={(e) => setFMotivo(e.target.value)} placeholder={evt?.stato === "ospedale" ? "Es. accertamenti, frattura, ..." : "Es. uscita con i parenti, visita medica, ..."} data-testid="mov-evento-motivo" />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block">Rientro previsto (facoltativo)</label>
              <div className="flex items-center gap-2">
                <Input type="number" min="0" value={fNum} onChange={(e) => setFNum(e.target.value)} placeholder="es. 3" className="w-28" data-testid="mov-evento-durata-num" />
                <div className="flex rounded-lg overflow-hidden border">
                  {["ore", "giorni"].map((u) => (
                    <button key={u} onClick={() => setFUnit(u)} className={`px-3 py-2 text-sm font-semibold transition-colors ${fUnit === u ? "bg-cyan-700 text-white" : "bg-secondary hover:bg-secondary/70"}`} data-testid={`mov-evento-unit-${u}`}>{u}</button>
                  ))}
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Tempo teorico in cui l'ospite rimarrà fuori/ricoverato.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEvt(null)}>Annulla</Button>
            <Button onClick={confermaEvento} className={evt?.stato === "ospedale" ? "bg-red-600 hover:bg-red-700" : "bg-amber-500 hover:bg-amber-600"} data-testid="mov-evento-conferma">
              {evt?.stato === "ospedale" ? "Registra ricovero" : "Registra uscita"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ProgrammaUscitaDialog open={progOpen} onClose={() => setProgOpen(false)} ospiti={attivi} operatore={signerName} onSaved={() => qc.invalidateQueries({ queryKey: ["uscite-programmate"] })} />
      <ConfirmUndoDialog ctx={confirmCtx} onClose={() => setConfirmCtx(null)} />
    </MeseGiorniEsplosi>
  );
}
